import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/next';
import { useRouter } from 'next/router';
import { DetailedHTMLProps, HTMLAttributes, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import useBookingParams from '../hooks/useBookingParams';
import { quartileToDate, setDayjsLocale } from '../lib/dayjs';
import {
  setAppVersion,
  setDay,
  setEmail,
  setEndQuartile,
  setHour,
  setInTheCircus,
  setIsApp,
  setKids,
  setLang,
  setMinute,
  setMonth,
  setName,
  setPhone,
  setRef,
  setRequestedTime,
  setSeatings,
  setStartQuartile,
  setYear,
} from '../services/paramsSlice';
import useRehydrationComplete from '../lib/utils/rehydrate';
import WrapperWithBackground from './_Theme/WrapperWithBackground';
import LoadingOverlay from './LoadingOverlay';
import { hasPersonalParams, withoutPersonalParams } from '../lib/utils/personalParams';

interface Props {
  children?: React.ReactNode;
  mainProps?: DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>;
  variant?: 'default' | 'filled';
  color?: string;
}

export default function Layout(props: Props) {
  const { children, variant = 'default', color = 'redOne', mainProps } = props;
  const router = useRouter();
  const { locale, route } = router;
  const dispatch = useDispatch();
  const { params } = useBookingParams();
  const isBookingFlow = route.includes('/new-reservation');
  const isRehydrated = useRehydrationComplete();

  useEffect(() => {
    setDayjsLocale(locale);
    dispatch(setLang(locale));
  }, [locale]);

  // Handle back button and back navigation state for iOS and Android apps. Using a javascript interface.
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof history !== 'undefined') {
      const backButtonEnabled = history.length > 1 && route !== '/venue/[venueId]/new-reservation';
      // @ts-ignore
      if (window?.webkit?.messageHandlers?.bookTable?.postMessage) {
        // @ts-ignore
        window?.webkit?.messageHandlers?.bookTable?.postMessage({
          backButtonEnabled,
        });
      }
      // @ts-ignore
      if (window?.BookTable?.setBackButtonEnabled) {
        // @ts-ignore
        window?.BookTable?.setBackButtonEnabled(backButtonEnabled);
      }
    }
  }, [route]);

  useEffect(() => {
    const { query } = router;

    // Mapping of query parameters to dispatch actions
    const queryActions = {
      app: (value: string | string[]) =>
        dispatch(setIsApp(Array.isArray(value) ? value[0] === 'true' : value === 'true')),
      appVersion: (value: string | string[]) =>
        dispatch(setAppVersion(Array.isArray(value) ? value[0] : value)),
      phone: (value: string | string[]) =>
        dispatch(setPhone(Array.isArray(value) ? value[0] : value)),
      name: (value: string | string[]) =>
        dispatch(setName(Array.isArray(value) ? value[0] : value)),
      email: (value: string | string[]) =>
        dispatch(setEmail(Array.isArray(value) ? value[0] : value)),
      inTheCircus: (value: string | string[]) =>
        dispatch(setInTheCircus(Array.isArray(value) ? value[0] === 'true' : value === 'true')),
      ref: (value: string | string[]) => dispatch(setRef(Array.isArray(value) ? value[0] : value)),
      startQuartile: (value: number | number[]) => {
        const quartile = Array.isArray(value) ? value[0] : value;
        dispatch(setStartQuartile(Number(quartile)));
        const dateFromQuartile = quartileToDate(quartile);
        dispatch(setHour(dateFromQuartile.format('HH')));
        dispatch(setMinute(dateFromQuartile.format('mm')));
        dispatch(setRequestedTime(dateFromQuartile.format('HH:mm')));
      },
      endQuartile: (value: number | number[]) =>
        dispatch(setEndQuartile(Number(Array.isArray(value) ? value[0] : value))),
      dateString: (value: string | string[]) => {
        dispatch(setYear(Array.isArray(value) ? value[0].slice(0, 4) : value.slice(0, 4)));
        dispatch(setMonth(Array.isArray(value) ? value[0].slice(4, 6) : value.slice(4, 6)));
        dispatch(setDay(Array.isArray(value) ? value[0].slice(6, 8) : value.slice(6, 8)));
      },
      seatings: (value: number | number[]) =>
        dispatch(setSeatings(Number(Array.isArray(value) ? value[0] : value))),
      kids: (value: number | number[]) =>
        dispatch(setKids(Number(Array.isArray(value) ? value[0] : value))),
    };

    // Iterate over the query parameters and call the appropriate action
    Object.entries(query).forEach(([key, value]) => {
      if (queryActions[key]) {
        queryActions[key](value);
      }
    });

    // The guest's details are in the store now; take them out of the URL.
    if (hasPersonalParams(query)) {
      router.replace(
        { pathname: router.pathname, query: withoutPersonalParams(query) },
        undefined,
        { shallow: true, scroll: false },
      );
    }
  }, [router.query, dispatch]);

  return (
    <>
      <WrapperWithBackground>
        <main
          {...mainProps}
          className={twMerge(
            mainProps?.className,
            variant === 'filled' ? `flex bg-${color}` : '',
            `flex flex-col ${
              params.isApp ? 'min-h-screen' : 'min-h-[100dvh]'
            } overflow-y-hidden w-full`,
          )}
        >
          {isBookingFlow && !isRehydrated ? <LoadingOverlay /> : children}
          <Analytics />
          <SpeedInsights />
        </main>
      </WrapperWithBackground>
    </>
  );
}
function setCount(arg0: number): any {
  throw new Error('Function not implemented.');
}
